import React from 'react';
import './RoadWidthPanel.css';

/**
 * Road Width Control Panel
 * Allows users to adjust road widths per road type with pixel-based inputs
 */
const RoadWidthPanel = ({ 
  roadTypes, 
  selectedRoadType, 
  onSelectRoadType, 
  currentZoom,
  startZoomLevel,
  widthValues, 
  onUpdateStartZoom,
  onUpdateWidth, 
  onApplyChanges,
  onReset, 
  onClose 
}) => {
  
  // Get all unique zoom levels from current width values
  const getAllZoomLevels = () => {
    const casingZooms = Object.keys(widthValues.casing).map(Number);
    const centerZooms = Object.keys(widthValues.center).map(Number);
    const baseZooms = Array.from({ length: 17 }, (_, idx) => idx + 4); // 4 to 20
    const allZooms = [...new Set([...baseZooms, ...casingZooms, ...centerZooms])];
    return allZooms.sort((a, b) => a - b);
  };

  const zoomLevels = getAllZoomLevels();

  const handleCasingChange = (zoom, value) => {
    if (value === '' || value === null || value === undefined) {
      // Remove the value if cleared
      onUpdateWidth('casing', zoom, undefined);
    } else {
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue >= 0) {
        onUpdateWidth('casing', zoom, numValue);
      }
    }
  };

  const handleCenterChange = (zoom, value) => {
    if (value === '' || value === null || value === undefined) {
      // Remove the value if cleared
      onUpdateWidth('center', zoom, undefined);
    } else {
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue >= 0) {
        onUpdateWidth('center', zoom, numValue);
      }
    }
  };

  return (
    <div className="road-width-panel">
      <div className="road-width-panel-header">
        <h3>Road Width Controls</h3>
        <button 
          className="road-width-close-btn"
          onClick={onClose}
          title="Close"
        >
          ×
        </button>
      </div>

      <div className="road-width-panel-content">
        {currentZoom !== null && (
          <div className="current-zoom-display">
            Current Zoom: <strong>{currentZoom}</strong>
          </div>
        )}

        {/* Road Type Selector */}
        <div className="road-width-control-group">
          <label htmlFor="road-type-select">
            Road Type
          </label>
          <select
            id="road-type-select"
            value={selectedRoadType}
            onChange={(e) => onSelectRoadType(e.target.value)}
            className="road-type-select"
          >
            {Object.entries(roadTypes).map(([key, type]) => (
              <option key={key} value={key}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {/* Starting Zoom Control */}
        <div className="road-width-control-group">
          <label htmlFor="road-start-zoom">
            Starting Zoom Level
          </label>
          <input
            id="road-start-zoom"
            type="number"
            min="0"
            max="24"
            step="0.1"
            value={startZoomLevel ?? ''}
            onChange={(e) => onUpdateStartZoom(e.target.value)}
            className="width-input"
          />
        </div>

        {/* Casing Width Controls */}
        <div className="road-width-control-group">
          <label className="section-label">
            Road Casing (Outline) - Width in Pixels
          </label>
          <div className="zoom-inputs-grid">
            {zoomLevels.map(zoom => (
              <div key={`casing-${zoom}`} className="zoom-input-row">
                <label htmlFor={`casing-z${zoom}`} className="zoom-label">
                  z{zoom}
                </label>
                <input
                  id={`casing-z${zoom}`}
                  type="number"
                  min="0"
                  max="50"
                  step="0.5"
                  value={widthValues.casing[zoom] || ''}
                  onChange={(e) => handleCasingChange(zoom, e.target.value)}
                  placeholder="Auto"
                  className="width-input"
                />
                <span className="unit-label">px</span>
              </div>
            ))}
          </div>
        </div>

        {/* Center Width Controls */}
        <div className="road-width-control-group">
          <label className="section-label">
            Road Center (Fill) - Width in Pixels
          </label>
          <div className="zoom-inputs-grid">
            {zoomLevels.map(zoom => (
              <div key={`center-${zoom}`} className="zoom-input-row">
                <label htmlFor={`center-z${zoom}`} className="zoom-label">
                  z{zoom}
                </label>
                <input
                  id={`center-z${zoom}`}
                  type="number"
                  min="0"
                  max="50"
                  step="0.5"
                  value={widthValues.center[zoom] || ''}
                  onChange={(e) => handleCenterChange(zoom, e.target.value)}
                  placeholder="Auto"
                  className="width-input"
                />
                <span className="unit-label">px</span>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="road-width-actions">
          <button 
            className="road-width-save-btn"
            onClick={onApplyChanges}
          >
            Apply Changes
          </button>
          
          <button 
            className="road-width-reset-btn"
            onClick={onReset}
          >
            Reset
          </button>
        </div>

        {/* Info Text */}
        <div className="road-width-info">
          <p>
            <strong>Tip:</strong> Enter pixel values for specific zoom levels. 
            Roads stay hidden below the starting zoom level.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RoadWidthPanel;
